/**
 * Populates the "Articles auteur" Swiper slider (and its mobile "view all"
 * list) with up to N articles by a given author, without relying on tags.
 *
 * The blog has too many articles for a single {% paginate %} pass (Shopify
 * caps it at 50/page), so the section renders the first page server-side
 * (fast path for authors with recent posts) and, if that wasn't enough,
 * this script fetches the remaining pages of templates/blog.author-feed.liquid
 * (?view=author-feed&page=N) client-side, filtering by author handle, until
 * it has enough slides or runs out of pages.
 *
 * Results are cached per author+blog in sessionStorage so navigating between
 * pages that show the same author's slider doesn't re-fetch every time.
 */
(function () {
  var CACHE_PREFIX = 'author-articles-slider:';
  var CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  function cacheKey(feedUrl, authorHandle) {
    return CACHE_PREFIX + feedUrl + ':' + authorHandle;
  }

  function readCache(feedUrl, authorHandle) {
    try {
      var raw = sessionStorage.getItem(cacheKey(feedUrl, authorHandle));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeCache(feedUrl, authorHandle, data) {
    try {
      data.savedAt = Date.now();
      sessionStorage.setItem(cacheKey(feedUrl, authorHandle), JSON.stringify(data));
    } catch (e) {
      /* storage full/unavailable: ignore, just skip caching */
    }
  }

  function fetchPage(feedUrl, page) {
    var sep = feedUrl.indexOf('?') > -1 ? '&' : '?';
    return fetch(feedUrl + sep + 'page=' + page).then(function (res) {
      if (!res.ok) throw new Error('author-feed fetch failed: ' + res.status);
      return res.text();
    });
  }

  function parseFeedHtml(html, authorHandle) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var items = doc.querySelectorAll(
      '.author-feed-item[data-author-handle="' + authorHandle + '"]'
    );
    var meta = doc.getElementById('author-feed-meta');
    return {
      matches: Array.prototype.map.call(items, function (item) {
        return { handle: item.dataset.handle, html: item.innerHTML };
      }),
      hasNext: !!meta && meta.dataset.hasNext === 'true',
    };
  }

  // Walks feed pages [startPage..maxPages], collecting articles by author.
  // Stops early once `limit` is reached (pass null to collect every match).
  function collectArticles(feedUrl, authorHandle, startPage, maxPages, limit) {
    var collected = [];
    var page = startPage;

    function step() {
      if (page > maxPages || (limit != null && collected.length >= limit)) {
        return Promise.resolve(collected);
      }
      return fetchPage(feedUrl, page)
        .then(function (html) {
          var result = parseFeedHtml(html, authorHandle);
          collected = collected.concat(result.matches);
          page++;
          if (!result.hasNext) return collected;
          return step();
        })
        .catch(function (err) {
          console.error(err);
          return collected;
        });
    }

    return step().then(function (all) {
      return limit != null ? all.slice(0, limit) : all;
    });
  }

  function articleToSlide(article) {
    var slide = document.createElement('div');
    slide.className = 'swiper-slide slideArticle rounded-md grid gap-4';
    slide.dataset.handle = article.handle;
    slide.innerHTML = article.html;
    return slide;
  }

  function initSwiper(root) {
    var swiperEl = root.querySelector('.swiper');
    if (!swiperEl || swiperEl.dataset.swiperInited || typeof Swiper === 'undefined') return;
    swiperEl.dataset.swiperInited = 'true';
    new Swiper(swiperEl, {
      centeredSlides: false,
      loop: false,
      slidesPerGroup: 1,
      slidesPerView: 8,
      speed: 500,
      navigation: {
        nextEl: root.querySelector('.swiper-next'),
        prevEl: root.querySelector('.swiper-prev'),
      },
      breakpoints: {
        0: {
          spaceBetween: 10,
          freeMode: true,
          slidesPerView: 1.3,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
        750: { spaceBetween: 12, freeMode: false, slidesPerView: 2 },
        1000: { spaceBetween: 20, freeMode: false, slidesPerView: 3 },
        1250: { spaceBetween: 20, freeMode: false, slidesPerView: 3 },
      },
    });
  }

  function shownHandles(root) {
    var handles = {};
    root.querySelectorAll('.swiper-wrapper [data-handle]').forEach(function (el) {
      handles[el.dataset.handle] = true;
    });
    return handles;
  }

  function initOne(root) {
    var wrapper = root.querySelector('.swiper-wrapper');
    var authorHandle = root.dataset.authorHandle;
    var feedUrl = root.dataset.feedUrl;
    var needed = parseInt(root.dataset.needed || '0', 10);
    var nextPage = parseInt(root.dataset.nextPage || '2', 10);
    var maxPages = parseInt(root.dataset.maxPages || '1', 10);
    // "true" only when the server-side scan of page 1 couldn't tell whether
    // this author has more than 5 articles in total (fewer than 6 found,
    // but later pages exist). Otherwise the "view all" button's visibility
    // was already decided server-side and JS leaves it alone.
    var moreCheckNeeded = root.dataset.moreCheck === 'true';
    var sliderLoader = root.querySelector('.author-articles-loader--overlay');

    function revealMoreButton() {
      root.querySelectorAll('.toggle-articles-btn').forEach(function (btn) {
        btn.classList.remove('hidden');
      });
    }

    function fillSlider() {
      if (!feedUrl || (needed <= 0 && !moreCheckNeeded)) {
        initSwiper(root);
        return Promise.resolve();
      }

      // Ask for one extra article beyond what the slider needs, as a
      // sentinel: if it comes back, there are more than 5 in total.
      var fetchLimit = moreCheckNeeded ? needed + 1 : needed;

      var cached = readCache(feedUrl, authorHandle);
      // Only worth showing a spinner when we're actually about to fetch
      // (a cache hit resolves instantly, so flashing one would just be noise).
      if (!cached && needed > 0 && sliderLoader) sliderLoader.classList.remove('hidden');

      var pending = cached
        ? Promise.resolve(cached)
        : collectArticles(feedUrl, authorHandle, nextPage, maxPages, fetchLimit).then(function (articles) {
            var result = { articles: articles, hasMore: moreCheckNeeded && articles.length > needed };
            writeCache(feedUrl, authorHandle, result);
            return result;
          });

      return pending.then(function (result) {
        result.articles.slice(0, needed).forEach(function (article) {
          wrapper.appendChild(articleToSlide(article));
        });
        if (result.hasMore) revealMoreButton();
        if (sliderLoader) sliderLoader.classList.add('hidden');
        initSwiper(root);
      });
    }

    fillSlider();

    // "Voir tous les articles": lazily fetch every remaining matching
    // article (skipping ones already shown in the slider) the first time
    // one of the toggle buttons is used.
    var container = root.querySelector('.articleContainer');
    var listLoader = container ? container.querySelector('.author-articles-loader') : null;
    var toggleButtons = root.querySelectorAll('.toggle-articles-btn');
    var fullListLoaded = false;

    toggleButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (fullListLoaded || !container || !feedUrl) return;
        fullListLoaded = true;

        if (listLoader) listLoader.classList.remove('hidden');
        container.setAttribute('aria-busy', 'true');
        collectArticles(feedUrl, authorHandle, 1, maxPages, null).then(function (articles) {
          var already = shownHandles(root);
          articles
            .filter(function (article) {
              return !already[article.handle];
            })
            .forEach(function (article) {
              container.insertAdjacentHTML('beforeend', article.html);
            });
          container.removeAttribute('aria-busy');
          if (listLoader) listLoader.classList.add('hidden');
        });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-author-articles-slider]').forEach(initOne);
  });
})();
